<template>
  <div id="custom" class="my-4">
    <h4 class="card-title">Suggest a custom dose plan</h4>
    <div>
      <VForm ref="observer" v-slot="{ meta: formMeta }" as="div">
        <b-form @submit.prevent="runSimulation">
          <b-input-group>
            <label title="Delivery Method">Delivery</label>
            <v-select
              id="method"
              ref="method"
              v-model="form.method"
              placeholder="Select a method"
              class="w-80 text-left"
              :options="methodList"
              :clearable="false"
              :reduce="(item: any) => item.code"
            ></v-select>
          </b-input-group>
          <b-input-group>
            <label title="Dose amount">Dose</label>
            <VField v-slot="{ meta }" v-model="form.amount" name="initialDose" :rules="doseRules">
              <b-form-input
                id="initDose"
                v-model="form.amount"
                type="number"
                :number="true"
                class="input-with-unit"
                placeholder="Amount"
                :state="getValidationState(meta)"
              />
            </VField>
            <b-form-select
              id="initDoseUnit"
              ref="doseUnit"
              v-model="form.amountUnit"
              class="unit-dropdown"
              :options="units.dose"
              :clearable="false"
            >
            </b-form-select>
          </b-input-group>
          <VErrorMessage name="initialDose" as="div" class="invalid-feedback d-block" />
          <b-input-group v-if="form.method === 'IV'">
            <label title="Infusion duration">Duration</label>
            <VField
              v-slot="{ meta }"
              v-model="form.duration"
              name="duration"
              :rules="durationRules"
            >
              <b-form-input
                id="duration"
                v-model="form.duration"
                type="number"
                :number="true"
                class="input-with-unit"
                placeholder="Infusion duration"
                :state="getValidationState(meta)"
              />
            </VField>
            <b-form-select
              id="durationUnit"
              ref="durationUnit"
              v-model="form.durationUnit"
              class="unit-dropdown"
              :options="units.duration"
              :clearable="false"
            >
            </b-form-select>
          </b-input-group>
          <VErrorMessage name="duration" as="div" class="invalid-feedback d-block" />
          <b-input-group v-if="form.method === 'IV'">
            <label title="Dosing interval">Interval</label>
            <VField
              v-slot="{ meta }"
              v-model="form.interval"
              name="interval"
              :rules="intervalRules"
            >
              <b-form-input
                id="interval"
                v-model="form.interval"
                type="number"
                :number="true"
                class="input-with-unit"
                placeholder="Dosing interval"
                :state="getValidationState(meta)"
              />
            </VField>
            <b-form-select
              id="intervalUnit"
              v-model="form.intervalUnit"
              class="unit-dropdown"
              :options="units.interval"
              :clearable="false"
            >
            </b-form-select>
          </b-input-group>
          <VErrorMessage name="interval" as="div" class="invalid-feedback d-block" />
          <div class="form-check form-switch float-end w-75" :v-if="hasAdministeredDoses">
            <input
              id="switch-loading"
              v-model="form.loading"
              class="form-check-input"
              type="checkbox"
            />
            <label for="switch-loading" class="form-check-label">Estimate a loading dose</label>
          </div>
          <b-row>
            <div class="float-end mb-5">
              <b-button
                class="btn-lg"
                variant="success"
                title="Run a simulation with custom data"
                :disabled="!formMeta.valid || !isFormValid"
                @click="runSimulation"
                >RUN CUSTOM
              </b-button>
            </div>
          </b-row>
          <p v-if="simulationGuidance" class="text-muted small mt-1">{{ simulationGuidance }}</p>
        </b-form>
      </VForm>
    </div>
    <div v-if="options.length > 0">
      <SimulationDoses :data="options" :method="form.method" :show-calculation="false" />
      <div class="float-end mb-5">
        <b-button variant="outline-secondary" title="Clear data" @click="clear"
          >CLEAR CUSTOM
        </b-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { Methods, units } from '@/models/utils/constants';
import { Custom } from '@/models/Custom';
import SimulationDoses from '@/components/display/SimulationDoses.vue';
import { useDosingStore } from '@/stores/dosing';
import type { DynamicSimulation } from '@/models/DynamicSimulation';

defineOptions({ name: 'CustomForm' });

const props = defineProps<{ sim: DynamicSimulation }>();

const store = useDosingStore();
const observer = ref<any>(null);
const options = ref<any[]>([]);

const drug = computed(() => store.getDrug);
const methodList = ref(getMethodList());

watch(
  () => props.sim,
  () => {
    options.value = [];
  },
);
watch(drug, () => {
  methodList.value = getMethodList();
});

function initForm() {
  return {
    amount: null as number | null,
    amountUnit: 'mg',
    duration: null as number | null,
    durationUnit: 'hours',
    interval: null as number | null,
    intervalUnit: 'hours',
    loading: false,
    method: null as string | null,
  };
}

const form = ref(initForm());

const doseRules = computed(() => ({
  doseBetween: { unit: form.value.amountUnit, model: props.sim?.model },
}));

const durationRules = computed(() => ({
  durationBetween: { unit: form.value.durationUnit },
}));

const intervalRules = computed(() => ({
  intervalBetween: true,
  intervalDuration: {
    duration: form.value.duration,
    durationUnit: form.value.durationUnit,
    intervalUnit: form.value.intervalUnit,
  },
}));

const isFormValid = computed(
  () =>
    !(
      form.value.amount === null ||
      (form.value.method === 'IV' && (form.value.duration === null || form.value.interval === null))
    ),
);

const hasAdministeredDoses = computed(() => props.sim?.hasAdministeredDoses());

const simulationGuidance = computed(() => {
  const effectiveMethod = form.value.method ?? store.getDelivery?.method ?? null;
  return (
    drug.value?.methods?.find((m: any) => m.type === effectiveMethod)?.simulationGuidance ?? null
  );
});

function getMethodList() {
  const list = Object.keys(Methods).map((m) => ({ code: m, label: (Methods as any)[m] }));
  if (drug.value) {
    const drugMethods = drug.value.methods.map((d: any) => d.type);
    return list.filter((m) => drugMethods.indexOf(m.code) > -1);
  }
  return list;
}

function nformat(value: number, decimals: number): string {
  return Number(Math.round(Number(value + 'e' + decimals)) + 'e-' + decimals).toFixed(decimals);
}

function clear() {
  options.value = [];
  form.value = initForm();
  store.clearSelectedPlan();
}

function getValidationState(meta: { validated: boolean; valid: boolean }): boolean | null {
  return meta.validated ? meta.valid : null;
}

function runSimulation(_$event: any) {
  store.clearSelectedPlan();
  nextTick(() => {
    if (props.sim) {
      const custom = new Custom({ ...form.value });
      const customPlan = props.sim.calculateCustom(custom, true);
      store.saveSelectedPlan(customPlan);
      const plans = [customPlan];
      const newPlans = plans.map((s: any) => {
        if (s.doseRegime.deliveryMethod === 'CONT') {
          return {
            Dose: s.doseRegime.dose,
            doseUnit: 'mg',
            AUC: nformat(s.mainResult.auc, 2),
            aucUnit: 'mcg*hr/mL',
            SS: nformat(s.mainResult.ss, 2),
            ssUnit: 'mcg/mL',
            plan: s,
            _rowVariant: s._rowVariant,
          };
        } else if (props.sim.model.getCompartment() === 2) {
          return {
            Interval: s.doseRegime.interval,
            intervalUnit: 'h',
            Dose: s.doseRegime.dose,
            doseUnit: 'mg',
            Infusion: s.doseRegime.infusionDuration,
            InfusionUnit: 'h',
            TMIC: nformat(s.mainResult.tmic, 2),
            Peak: nformat(s.mainResult.peak, 2),
            peakUnit: 'mcg/mL',
            plan: s,
            _rowVariant: s._rowVariant,
          };
        } else {
          return {
            Interval: s.doseRegime.interval,
            intervalUnit: 'h',
            Dose: s.doseRegime.dose,
            doseUnit: 'mg',
            Infusion: s.doseRegime.infusionDuration,
            InfusionUnit: 'h',
            AUC: nformat(s.mainResult.auc, 2),
            aucUnit: 'mcg*hr/mL',
            Peak: nformat(s.mainResult.peak, 2),
            peakUnit: 'mcg/mL',
            Trough: nformat(s.mainResult.trough, 2),
            troughUnit: 'mcg/mL',
            plan: s,
            _rowVariant: s._rowVariant,
          };
        }
      });
      options.value = [
        ...options.value.filter((o: any) => o.plan.doseRegime.deliveryMethod === form.value.method),
        ...newPlans,
      ];
    }
  });
}
</script>

<style scoped>
#custom form {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 480px;
  margin: 0 auto;
}

.input-group {
  justify-content: start !important;
}
</style>
